/**
 * Shared display helpers for the WhatsApp Inbox (FE-07+).
 *
 * Mirrors the 24h session-window math the backend enforces
 * (app/utils/whatsapp_session.py) purely for display — the composer never
 * trusts this to decide whether a send is allowed; the backend response's
 * `window_closed` flag is the actual gate (scope.txt §8: "backend should
 * enforce the rule; it should not rely only on frontend validation").
 */
const SESSION_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Every timestamp this API returns (orjson-serialized naive datetime, e.g.
 * "2026-09-07T10:55:20.698767") is IST wall-clock time with no zone suffix
 * (CLAUDE.md: "every TIMESTAMP WITHOUT TIME ZONE column in this DB stores
 * IST"). Appending "Z" — treating it as UTC — was the bug here: it shifted
 * every displayed time 5.5 hours late and threw off the 24h session-window
 * countdown by the same amount. Appending the real "+05:30" offset instead
 * gives a Date with the correct underlying instant, so both display
 * (toLocaleTimeString in the viewer's own zone) and elapsed-time math
 * (Date.now() - d.getTime()) come out right regardless of the viewer's
 * browser timezone.
 */
const parseIstTimestamp = (iso: string): Date =>
  new Date(iso.endsWith('Z') || /[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}+05:30`);

export const getInitials = (name: string | null | undefined): string => {
  const trimmed = (name || '').trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
  return (first + last).toUpperCase() || '?';
};

const AVATAR_PALETTE = [
  { bg: 'bg-[#153d23]', text: 'text-white' },
  { bg: 'bg-indigo-100', text: 'text-indigo-800' },
  { bg: 'bg-amber-100', text: 'text-amber-800' },
  { bg: 'bg-red-100', text: 'text-red-800' },
  { bg: 'bg-gray-100', text: 'text-gray-600' },
  { bg: 'bg-sky-100', text: 'text-sky-800' },
  { bg: 'bg-pink-100', text: 'text-pink-800' },
];

/** Deterministic per-contact color so a conversation's avatar stays the same
 * color across renders and reloads, without storing anything. */
export const avatarColorFor = (key: string): { bg: string; text: string } => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

export const formatRelativeTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = parseIstTimestamp(iso);
  if (isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin} min`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay} days`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

export const formatMessageTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = parseIstTimestamp(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

export const formatDateSeparator = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = parseIstTimestamp(iso);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
};

export interface WindowInfo {
  open: boolean;
  /** "23h 12m left" or "closed Xh Ym ago" */
  label: string;
  minutesLeft: number;
}

/** Display-only mirror of is_session_window_open — never the source of truth. */
export const sessionWindowInfo = (lastInboundAt: string | null | undefined): WindowInfo => {
  if (!lastInboundAt) return { open: false, label: 'No inbound message yet', minutesLeft: 0 };
  const inbound = parseIstTimestamp(lastInboundAt);
  if (isNaN(inbound.getTime())) return { open: false, label: '', minutesLeft: 0 };
  const closesAt = inbound.getTime() + SESSION_WINDOW_MS;
  const msLeft = closesAt - Date.now();
  const minutesLeft = Math.floor(Math.abs(msLeft) / 60_000);
  const h = Math.floor(minutesLeft / 60);
  const m = minutesLeft % 60;
  if (msLeft > 0) return { open: true, label: `${h}h ${m}m left`, minutesLeft };
  return { open: false, label: `closed ${h}h ${m}m ago`, minutesLeft: 0 };
};

export const LEAD_BUCKET_BADGE: Record<string, { label: string; bg: string; text: string }> = {
  abandoned_cart: { label: 'Abandoned Cart', bg: 'bg-blue-50', text: 'text-blue-700' },
  reorder: { label: 'Reorder', bg: 'bg-green-50', text: 'text-green-700' },
  rto: { label: 'RTO', bg: 'bg-red-50', text: 'text-red-700' },
  phone: { label: 'Phone Lead', bg: 'bg-gray-100', text: 'text-gray-600' },
};

export const CONVERSATION_STATUS_BADGE: Record<string, { label: string; bg: string; text: string; border?: string }> = {
  open: { label: 'Open', bg: 'bg-gray-50', text: 'text-gray-600' },
  resolved: { label: 'Resolved', bg: 'bg-green-50', text: 'text-green-700', border: 'border border-green-200' },
  archived: { label: 'Archived', bg: 'bg-gray-100', text: 'text-gray-500' },
};

export const MESSAGE_STATUS_COLOR: Record<string, string> = {
  sending: 'text-gray-400',
  sent: 'text-gray-400',
  delivered: 'text-gray-400',
  read: 'text-blue-500',
  failed: 'text-red-600',
};
